<template>
  <main class="site-shell">
    <div class="page-bg" aria-hidden="true"></div>
    <div class="grid-bg" aria-hidden="true"></div>

    <section id="home" class="hero section" aria-labelledby="hero-title">
      <div class="container">
        <div v-reveal="{ delay: 100 }" class="eyebrow">
          <Sparkles :size="15" aria-hidden="true" />
          Content Creator aus Langnau
        </div>

        <div class="hero__grid">
          <div class="hero__main">
            <div
              v-reveal="{ delay: 180, y: 32, duration: 850 }"
              class="hero__brand"
            >
              <h1 id="hero-title" class="sr-only">
                Krilin Schmid – Fotografie, Videografie und Webdesign
              </h1>

              <img
                :src="heroLogo"
                alt=""
                class="hero__logo"
                aria-hidden="true"
                fetchpriority="high"
                decoding="async"
              >
            </div>

            <div v-reveal="{ delay: 260, y: 24, duration: 760 }" class="hero__meta" aria-label="Leistungsbereiche">
              <span>Sport</span>
              <span>Teams</span>
              <span>Events</span>
            </div>
          </div>

          <div v-reveal="{ delay: 340, y: 26, duration: 760 }" class="hero__copy">
            <p>
              Ich bin Krilin. Ich produziere Fotografie, Video und Webauftritte,
              die direkt wirken — authentisch, klar und bereit für Club,
              Website und Social Media.
            </p>

            <div class="hero__actions">
              <button
                type="button"
                class="button button--accent"
                @click="scrollToSection('work')"
              >
                Arbeiten ansehen
                <ArrowUpRight :size="18" aria-hidden="true" />
              </button>

              <button
                type="button"
                class="button button--ghost"
                @click="scrollToSection('contact')"
              >
                Projekt starten
              </button>
            </div>

            <p class="hero__note">
              Für Clubs, Athlet:innen, Unternehmen und Veranstaltungen.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section id="services" class="section services" aria-labelledby="services-title">
      <div class="container">
        <div class="section-header">
          <div>
            <p class="section-kicker">Services</p>
          </div>
          <div>
            <h2 id="services-title">Was ich für dich umsetze.</h2>
            <p class="section-header__text">
              Drei Leistungen, ein konsistenter Auftritt: vom entscheidenden Moment
              bis zur fertigen digitalen Präsentation.
            </p>
          </div>
        </div>

        <div class="services-grid">
          <article
            v-for="(service, index) in services"
            :key="service.title"
            v-reveal="{ delay: index * 110 }"
            class="service-card"
          >
            <div class="service-card__topline">
              <span>0{{ index + 1 }}</span>
              <component :is="service.icon" :size="24" stroke-width="1.8" aria-hidden="true" />
            </div>

            <div>
              <h3>{{ service.title }}</h3>
              <p>{{ service.text }}</p>
            </div>

            <ul>
              <li v-for="point in service.points" :key="point">
                <Check :size="15" aria-hidden="true" />
                {{ point }}
              </li>
            </ul>
          </article>
        </div>
      </div>
    </section>

    <section id="about" class="section about" aria-labelledby="about-title">
      <div class="container about__grid">
        <div v-reveal class="about-photo image-block image-block--tall">
          <img
            :src="portraitImage"
            alt="Portrait von Krilin Schmid"
            class="about-photo__image"
            loading="eager"
            decoding="async"
          >

          <div class="about-photo__overlay" aria-hidden="true"></div>

          <div class="image-block__content">
            <p>Fotograf · Videograf · Webdesigner</p>
            <h3>Krilin Schmid</h3>
          </div>
        </div>

        <div v-reveal="{ delay: 150 }" class="about__content">
          <p class="section-kicker">About</p>
          <h2 id="about-title">Nah dran. Ohne künstlich zu wirken.</h2>

          <div class="about__text">
            <p>
              Mein Schwerpunkt liegt auf Sport, Teams und Veranstaltungen.
              Ich suche nicht nach gestellten Situationen, sondern nach Energie,
              Fokus und den kleinen Reaktionen, die eine Geschichte glaubwürdig machen.
            </p>

            <p>
              Die Produktion bleibt unkompliziert, die Bildsprache klar und die
              Übergabe so organisiert, dass dein Content direkt eingesetzt werden kann.
            </p>
          </div>

          <button
            type="button"
            class="button button--ghost about-more-button"
            @click="showAboutModal = true"
          >
            Mehr über mich
            <ArrowUpRight :size="18" aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>

    <section class="logo-trustbar" aria-label="Ausgewählte Zusammenarbeit">
      <div class="container logo-trustbar__inner">
        <p class="logo-trustbar__title">
          Ausgewählte Clubs und Unternehmen
        </p>

        <div class="logo-trustbar__logos">
          <div class="logo-trustbar__track">
            <div
              v-for="loop in 2"
              :key="`logo-loop-${loop}`"
              class="logo-trustbar__group"
              :aria-hidden="loop === 2 ? 'true' : undefined"
            >
              <a
                v-for="brand in trustedBy"
                :key="`${loop}-${brand.name}`"
                href="#work"
                class="logo-trustbar__item"
                :aria-label="loop === 1 ? `${brand.name} – zu den Arbeiten` : undefined"
                :title="loop === 1 ? brand.name : undefined"
                :tabindex="loop === 2 ? -1 : undefined"
              >
                <img
                  :src="brand.logo"
                  :alt="loop === 1 ? brand.name : ''"
                  class="logo-trustbar__logo"
                  loading="lazy"
                  decoding="async"
                >
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="work" class="section work" aria-labelledby="work-title">
      <div class="container">
        <div class="section-header">
          <div>
            <p class="section-kicker">Work</p>
          </div>
          <div>
            <h2 id="work-title">Ausgewählte Arbeiten.</h2>
            <p class="section-header__text">
              Eine wechselnde, kuratierte Auswahl meiner Arbeiten. Die nächste Bildauswahl
              wird bereits im Hintergrund geladen, damit der Wechsel direkt und ohne Verzögerung wirkt.
            </p>
          </div>
        </div>

        <div v-if="visibleWorkImages.length" class="work-grid">
          <button
            v-for="(work, index) in visibleWorkImages"
            :key="`${work.id}-${workGalleryGeneration}`"
            type="button"
            class="work-grid__item"
            :class="[
              `work-grid__item--${workLayout[index] || 'small'}`,
              { 'is-loaded': loadedWorkImageIds.has(work.id) },
            ]"
            :style="{ '--work-index': index }"
            :aria-label="`Projektaufnahme ${index + 1} vergrößern`"
            @click="openWorkImage(index)"
          >
            <img
              :src="work.image"
              :alt="`Ausgewählte Projektaufnahme ${index + 1} von Krilin Schmid`"
              class="work-grid__image"
              :class="{ 'is-loaded': loadedWorkImageIds.has(work.id) }"
              :loading="index < 2 ? 'eager' : 'lazy'"
              decoding="async"
              @load="markWorkImageLoaded(work.id, index, workGalleryGeneration)"
              @error="markWorkImageLoaded(work.id, index, workGalleryGeneration)"
            >
            <span class="work-grid__zoom" aria-hidden="true">
              <ArrowUpRight :size="18" />
            </span>
          </button>
        </div>

        <div v-else class="work-empty" role="status">
          Keine Work-Bilder gefunden. Prüfe den Ordner
          <strong>src/assets/images/work/</strong>.
        </div>

        <div class="work-actions">
          <button
            v-if="workImages.length > WORK_IMAGE_COUNT"
            type="button"
            class="button button--ghost"
            @click="showMoreWorkImages"
          >
            <RefreshCw :size="18" aria-hidden="true" />
            Noch nicht überzeugt? Zeig mir mehr.
          </button>

          <a
            href="https://krilinschmid.mypixieset.com/"
            target="_blank"
            rel="noopener noreferrer"
            class="button button--accent"
          >
            Alle Galerien ansehen
            <ArrowUpRight :size="18" aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>

    <section class="section process" aria-labelledby="process-title">
      <div class="container">
        <div class="section-header section-header--compact">
          <div>
            <p class="section-kicker">Ablauf</p>
          </div>
          <div>
            <h2 id="process-title">Einfach vom Briefing bis zur Übergabe.</h2>
          </div>
        </div>

        <div class="process-grid">
          <article
            v-for="(step, index) in processSteps"
            :key="step.title"
            v-reveal="{ delay: index * 100 }"
            class="process-card"
          >
            <span class="process-card__number">0{{ index + 1 }}</span>
            <h3>{{ step.title }}</h3>
            <p>{{ step.text }}</p>
          </article>
        </div>
      </div>
    </section>

    <!--<PricingSection
      @request="showContactModal = true"
    /> -->


    <section id="contact" class="section contact" aria-labelledby="contact-title">
      <div class="container">
        <div v-reveal class="contact-card">
          <div>
            <p class="section-kicker section-kicker--light">Kontakt</p>
            <h2 id="contact-title">Bereit für dein Projekt?</h2>

            <p>
              Erzähl mir kurz, was du vorhast. Ich melde mich mit einer klaren
              Einschätzung zu Idee, Umfang und nächstem Schritt.

              Jedes Projekt ist anders. Dauer, Ort, gewünschte Bildmenge und Verwendungszweck beeinflussen den Aufwand. Nach einer kurzen Anfrage erhältst du von mir eine transparente und unverbindliche Offerte, die genau zu deinem Projekt passt.
            </p>
          </div>

          <div class="contact-card__actions">
            <button
              type="button"
              class="button button--light"
              @click="showContactModal = true"
            >
              <Mail :size="18" aria-hidden="true" />
              Nachricht schreiben
            </button>

            <a
              href="https://www.instagram.com/krilin.schmid/"
              target="_blank"
              rel="noopener noreferrer"
              class="button button--dark-ghost"
            >
              <Instagram :size="18" aria-hidden="true" />
              Instagram
            </a>
          </div>
        </div>

        <footer class="footer">
          <p>© 2026 Krilin Schmid · Fotografie, Videografie & Webdesign</p>
          <p>Langnau, Schweiz</p>
        </footer>
      </div>
    </section>

    <transition name="lightbox-fade">
      <div
        v-if="selectedWork"
        class="lightbox"
        role="dialog"
        aria-modal="true"
        aria-label="Vergrößerte Projektaufnahme"
        @click.self="closeWorkImage"
      >
        <button
          type="button"
          class="lightbox__close"
          aria-label="Ansicht schließen"
          @click="closeWorkImage"
        >
          <X :size="22" aria-hidden="true" />
        </button>

        <button
          v-if="visibleWorkImages.length > 1"
          type="button"
          class="lightbox__nav lightbox__nav--previous"
          aria-label="Vorheriges Bild"
          @click="showPreviousWork"
        >
          <ChevronLeft :size="24" aria-hidden="true" />
        </button>

        <figure class="lightbox__figure">
          <img
            :src="selectedWork.image"
            :alt="`Vergrößerte Projektaufnahme ${selectedWorkIndex + 1}`"
            class="lightbox__image"
          >
          <figcaption>
            {{ selectedWorkIndex + 1 }} / {{ visibleWorkImages.length }}
          </figcaption>
        </figure>

        <button
          v-if="visibleWorkImages.length > 1"
          type="button"
          class="lightbox__nav lightbox__nav--next"
          aria-label="Nächstes Bild"
          @click="showNextWork"
        >
          <ChevronRight :size="24" aria-hidden="true" />
        </button>
      </div>
    </transition>

    <!--
      Modals werden direkt unter <body> gerendert.
      Dadurch können sie auf kleinen Displays nicht mehr durch
      .site-shell { overflow: clip } abgeschnitten werden.
    -->
    <Teleport to="body">
      <AboutMeModal
        :open="showAboutModal"
        @close="showAboutModal = false"
      />
      <ContactModal
        :open="showContactModal"
        @close="showContactModal = false"
      />
    </Teleport>
  </main>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import {
  ArrowUpRight,
  Camera,
  Check,
  ChevronLeft,
  ChevronRight,
  Instagram,
  Mail,
  Monitor,
  RefreshCw,
  Sparkles,
  Video,
  X,
} from 'lucide-vue-next'

import portraitImage from '@/assets/images/KSC09560.webp'
import heroLogo from '@/assets/images/logos/krilin/KS_Logo_Blau.png'

import logoTigers from '@/assets/images/logos/unihockey_Tigers.png'
import logoLejon from '@/assets/images/logos/lejon.png'
import logoSCBern from '@/assets/images/logos/scb.png'
import logoKonolfingen from '@/assets/images/logos/sc_konolfingen_farbig.png'
import logoTurnverein from '@/assets/images/logos/logo_tvb.png'
import logoKensekai from '@/assets/images/logos/kensekai_karateschule.png'
import logoHolger from '@/assets/images/logos/kaeptn_holger.png'
import logoSCL from '@/assets/images/logos/Logo_SCL.png'

import AboutMeModal from '@/components/AboutMeModal.vue'
import ContactModal from '@/components/ContactModal.vue'

const showAboutModal = ref(false)
const showContactModal = ref(false)
const selectedWorkIndex = ref(null)

const workImageContext = require.context(
  '@/assets/images/work',
  false,
  /\.(png|jpg|jpeg|webp)$/
)

const workImages = workImageContext
  .keys()
  .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  .map((key) => ({
    id: key,
    image: workImageContext(key),
  }))

const WORK_IMAGE_COUNT = 9

function shuffleImages(images) {
  const shuffled = [...images]

  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    const current = shuffled[i]

    shuffled[i] = shuffled[j]
    shuffled[j] = current
  }

  return shuffled
}

function createWorkSelection(previousSelection = []) {
  if (workImages.length <= WORK_IMAGE_COUNT) {
    return shuffleImages(workImages)
  }

  const previousIds = new Set(previousSelection.map((work) => work.id))

  // Bevorzugt Bilder, die in der aktuellen Auswahl noch nicht sichtbar sind.
  const newImages = shuffleImages(
    workImages.filter((work) => !previousIds.has(work.id))
  )
  const previousImages = shuffleImages(
    workImages.filter((work) => previousIds.has(work.id))
  )

  return shuffleImages([...newImages, ...previousImages].slice(0, WORK_IMAGE_COUNT))
}

const MIN_WORK_LOADING_TIME = 360
const WORK_REVEAL_STAGGER = 55

const visibleWorkImages = ref(createWorkSelection())
const loadedWorkImageIds = ref(new Set())
const workGalleryGeneration = ref(0)

function markWorkImageLoaded(id, index, generation) {
  // Selbst Bilder aus dem Browser-Cache bleiben kurz im Ladezustand.
  // Dadurch ist die Animation immer sichtbar und wirkt nicht zufällig.
  const revealDelay =
    MIN_WORK_LOADING_TIME + index * WORK_REVEAL_STAGGER

  window.setTimeout(() => {
    // Alte Timeouts einer vorherigen Bildauswahl dürfen nichts mehr verändern.
    if (generation !== workGalleryGeneration.value) return

    const isStillVisible = visibleWorkImages.value.some(
      (work) => work.id === id
    )

    if (!isStillVisible || loadedWorkImageIds.value.has(id)) return

    const nextLoadedIds = new Set(loadedWorkImageIds.value)
    nextLoadedIds.add(id)
    loadedWorkImageIds.value = nextLoadedIds
  }, revealDelay)
}

const selectedWork = computed(() => {
  if (selectedWorkIndex.value === null) return null
  return visibleWorkImages.value[selectedWorkIndex.value] || null
})

const workLayout = [
  'feature',
  'portrait',
  'small',
  'small',
  'wide',
  'portrait',
  'small',
  'small',
  'wide',
]

const services = [
  {
    title: 'Fotografie',
    text: 'Authentische Bilder mit Tempo, Nähe und einer klaren visuellen Linie.',
    icon: Camera,
    points: ['Sport & Teams', 'Events & Konzerte', 'Portraits & Reportagen'],
  },
  {
    title: 'Videografie',
    text: 'Kurze, dynamische Formate, die auf Social Media und Websites funktionieren.',
    icon: Video,
    points: ['Reels & Social Clips', 'Aftermovies', 'Club- und Eventcontent'],
  },
  {
    title: 'Webdesign',
    text: 'Reduzierte Websites, die deine Arbeiten stark zeigen und einfach bedienbar bleiben.',
    icon: Monitor,
    points: ['Portfolio-Websites', 'Responsive Umsetzung', 'Klare Nutzerführung'],
  },
]

const processSteps = [
  {
    title: 'Briefing',
    text: 'Wir klären Ziel, Einsatzbereich, Stil und Umfang — kurz, konkret und ohne Umwege.',
  },
  {
    title: 'Produktion',
    text: 'Ich arbeite selbstständig, unaufdringlich und mit Blick auf die Momente, die wirklich zählen.',
  },
  {
    title: 'Übergabe',
    text: 'Du erhältst sauber aufbereitete Dateien, die direkt für Web, Social Media oder Kommunikation bereitstehen.',
  },
]

const trustedBy = [
  { name: 'Unihockey Tigers', logo: logoTigers },
  { name: 'Unihockey Lejon Zäziwil', logo: logoLejon },
  { name: 'SC Bern', logo: logoSCBern },
  { name: 'SC Konolfingen', logo: logoKonolfingen },
  { name: 'Turnverein Bärau', logo: logoTurnverein },
  { name: 'Kensekai Karateschule', logo: logoKensekai },
  { name: "Käpt'n Holger", logo: logoHolger },
  { name: "Logo Tigers", logo: logoSCL}
]

let previousBodyOverflow = ''
let preparedWorkImages = []
let isPreparingWorkImages = false

function preloadImage(src) {
  return new Promise((resolve) => {
    const image = new Image()

    const finish = () => resolve()
    image.onload = finish
    image.onerror = finish
    image.src = src

    // Falls das Bild bereits im Browser-Cache liegt.
    if (image.complete) finish()
  })
}

async function prepareNextWorkSelection() {
  if (
    typeof window === 'undefined' ||
    workImages.length <= WORK_IMAGE_COUNT ||
    isPreparingWorkImages
  ) {
    return
  }

  isPreparingWorkImages = true

  const nextSelection = createWorkSelection(visibleWorkImages.value)

  // Erst alle Bilder der nächsten Auswahl laden ...
  await Promise.all(nextSelection.map((work) => preloadImage(work.image)))

  // ... und erst danach für den nächsten Wechsel bereithalten.
  preparedWorkImages = nextSelection
  isPreparingWorkImages = false
}

async function showMoreWorkImages() {
  // Falls die nächste Auswahl wider Erwarten noch nicht fertig ist,
  // wird sie spätestens beim Klick vollständig geladen.
  if (!preparedWorkImages.length) {
    await prepareNextWorkSelection()
  }

  if (!preparedWorkImages.length) return

  // Die neue Auswahl startet kurz im minimalistischen Ladezustand.
  // Da die Dateien bereits vorgeladen wurden, folgt das Fade praktisch sofort.
  loadedWorkImageIds.value = new Set()
  workGalleryGeneration.value += 1
  visibleWorkImages.value = preparedWorkImages
  preparedWorkImages = []

  // Sofort wieder die darauffolgende Auswahl im Hintergrund vorbereiten.
  prepareNextWorkSelection()
}

function scrollToSection(id) {
  const target = document.getElementById(id)
  if (!target) return

  const headerOffset = 100
  const targetPosition =
    target.getBoundingClientRect().top + window.scrollY - headerOffset

  window.scrollTo({
    top: targetPosition,
    behavior: 'smooth',
  })
}

function openWorkImage(index) {
  selectedWorkIndex.value = index
}

function closeWorkImage() {
  selectedWorkIndex.value = null
}

function showPreviousWork() {
  const length = visibleWorkImages.value.length
  if (!length || selectedWorkIndex.value === null) return

  selectedWorkIndex.value = (selectedWorkIndex.value - 1 + length) % length
}

function showNextWork() {
  const length = visibleWorkImages.value.length
  if (!length || selectedWorkIndex.value === null) return

  selectedWorkIndex.value = (selectedWorkIndex.value + 1) % length
}

function handleKeydown(event) {
  if (!selectedWork.value) return

  if (event.key === 'Escape') closeWorkImage()
  if (event.key === 'ArrowLeft') showPreviousWork()
  if (event.key === 'ArrowRight') showNextWork()
}

watch(selectedWork, (image) => {
  if (typeof document === 'undefined') return

  if (image) {
    previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = previousBodyOverflow
  }
})

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)

  // Schon nach dem ersten Rendern eine komplette zweite Auswahl laden.
  // Dadurch reagiert der "Zeig mir mehr"-Button in der Regel sofort.
  prepareNextWorkSelection()
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = previousBodyOverflow
})
</script>

<style scoped>
/* LOGO-BLAU: #373D6B · RGB 55, 61, 107 */
@font-face {
  font-family: 'Halenoir';
  src: url('/fonts/Halenoir.woff2') format('woff2');
  font-weight: 400 900;
  font-display: swap;
}

:global(html) {
  scroll-behavior: smooth;
  background: #f7f9fd;
}

:global(body) {
  margin: 0;
  min-height: 100%;
  background: #f7f9fd;
}

:global(body > [data-v-app]) {
  min-height: 100%;
}

:global(button),
:global(a) {
  -webkit-tap-highlight-color: transparent;
}

:global(::selection) {
  background: rgba(55, 61, 107, 0.2);
  color: #171a31;
}

* {
  box-sizing: border-box;
}

.site-shell {
  --background: #f7f9fd;
  --surface: #ffffff;
  --surface-soft: #f0f1f7;
  --navy: #171a31;
  --navy-soft: #242a4c;
  --blue: #373d6b;
  --blue-dark: #292e52;
  --blue-light: #626c9f;
  --text: #171a31;
  --text-soft: #4d5d75;
  --text-muted: #77869c;
  --line: rgba(41, 46, 82, 0.12);
  --line-strong: rgba(55, 61, 107, 0.28);
  --shadow-soft: 0 20px 60px rgba(41, 46, 82, 0.09);
  --shadow-blue: 0 22px 60px rgba(55, 61, 107, 0.17);

  position: relative;
  isolation: isolate;
  min-height: 100vh;
  overflow: clip;
  background: var(--background);
  color: var(--text);
  font-family: 'Halenoir', Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  text-rendering: optimizeLegibility;
}

.page-bg,
.grid-bg {
  position: fixed;
  inset: 0;
  pointer-events: none;
}

.page-bg {
  z-index: -2;
  background:
    radial-gradient(circle at 6% 9%, rgba(55, 61, 107, 0.13), transparent 25rem),
    radial-gradient(circle at 95% 18%, rgba(98, 108, 159, 0.13), transparent 31rem),
    linear-gradient(180deg, #ffffff 0%, #fafafe 48%, #f5f5f9 100%);
}

.grid-bg {
  z-index: -1;
  opacity: 0.48;
  mask-image: linear-gradient(to bottom, #000 0%, transparent 78%);
  background-image:
    linear-gradient(to right, rgba(55, 61, 107, 0.04) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(55, 61, 107, 0.04) 1px, transparent 1px);
  background-size: 76px 76px;
}

.container {
  position: relative;
  width: min(calc(100% - 48px), 1240px);
  margin-inline: auto;
}

.section {
  position: relative;
  padding: 112px 0;
  scroll-margin-top: 100px;
}

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 50px;
  padding: 0 24px;
  border: 1px solid transparent;
  border-radius: 999px;
  font: inherit;
  font-size: 0.92rem;
  font-weight: 850;
  line-height: 1;
  text-decoration: none;
  cursor: pointer;
  transition:
    transform 180ms ease,
    border-color 180ms ease,
    background 180ms ease,
    color 180ms ease,
    box-shadow 180ms ease;
}

.button:hover {
  transform: translateY(-2px);
}

.button:focus-visible,
.work-grid__item:focus-visible,
.lightbox button:focus-visible {
  outline: 3px solid rgba(55, 61, 107, 0.32);
  outline-offset: 3px;
}

.button--accent {
  background: linear-gradient(135deg, #626c9f, var(--blue) 55%, var(--blue-dark));
  color: #ffffff;
  box-shadow:
    0 14px 30px rgba(55, 61, 107, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.23);
}

.button--accent:hover {
  box-shadow:
    0 18px 38px rgba(55, 61, 107, 0.31),
    inset 0 1px 0 rgba(255, 255, 255, 0.28);
}

.button--ghost {
  border-color: var(--line-strong);
  background: rgba(255, 255, 255, 0.8);
  color: var(--text);
  box-shadow: 0 10px 28px rgba(41, 46, 82, 0.06);
  backdrop-filter: blur(14px);
}

.button--ghost:hover {
  border-color: rgba(55, 61, 107, 0.48);
  background: var(--surface-soft);
  color: var(--blue-dark);
}

.button--light {
  background: #ffffff;
  color: var(--navy);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.2);
}

.button--light:hover {
  background: #eceef6;
  color: var(--blue-dark);
}

.button--dark-ghost {
  border-color: rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.07);
  color: #ffffff;
}

.button--dark-ghost:hover {
  border-color: rgba(255, 255, 255, 0.42);
  background: rgba(255, 255, 255, 0.12);
}

.eyebrow,
.section-kicker {
  color: var(--blue);
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0.25em;
  text-transform: uppercase;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 34px;
  padding: 10px 15px;
  border: 1px solid rgba(55, 61, 107, 0.2);
  border-radius: 999px;
  background: rgba(240, 241, 247, 0.9);
  box-shadow: 0 9px 25px rgba(55, 61, 107, 0.07);
}

.hero {
  display: flex;
  align-items: center;
  min-height: 91svh;
  padding-top: 156px;
  padding-bottom: 92px;
}

.hero__grid {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(310px, 0.68fr);
  align-items: end;
  gap: clamp(54px, 8vw, 112px);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.hero__brand {
  width: 100%;
  max-width: 900px;
}

.hero__logo {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
  object-position: left center;
  filter: drop-shadow(0 20px 45px rgba(55, 61, 107, 0.08));
}

.hero__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 34px;
}

.hero__meta span {
  padding: 8px 13px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.76);
  color: var(--text-soft);
  font-size: 0.78rem;
  font-weight: 800;
  box-shadow: 0 8px 22px rgba(41, 46, 82, 0.05);
}

.hero__copy {
  padding-bottom: 4px;
}

.hero__copy > p:first-child {
  max-width: 520px;
  margin: 0;
  color: var(--text-soft);
  font-size: clamp(1.04rem, 1.25vw, 1.16rem);
  line-height: 1.76;
}

.hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 32px;
}

.hero__note {
  margin: 22px 0 0;
  color: var(--text-muted);
  font-size: 0.84rem;
  line-height: 1.5;
}

.section-header {
  display: grid;
  grid-template-columns: 0.28fr 1fr;
  align-items: end;
  gap: 32px;
  margin-bottom: 50px;
}

.section-header--compact {
  margin-bottom: 38px;
}

.section-header h2,
.about__content h2,
.contact-card h2 {
  margin: 0;
  color: var(--navy);
  font-weight: 900;
  letter-spacing: -0.06em;
  line-height: 0.97;
}

.section-header h2 {
  max-width: 890px;
  font-size: clamp(3.1rem, 5.2vw, 5.2rem);
}

.section-header__text {
  max-width: 690px;
  margin: 20px 0 0;
  color: var(--text-soft);
  font-size: 1.04rem;
  line-height: 1.75;
}

.services {
  background: linear-gradient(180deg, rgba(240, 241, 247, 0.56), rgba(255, 255, 255, 0));
  border-block: 1px solid rgba(55, 61, 107, 0.07);
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.service-card {
  display: flex;
  min-height: 410px;
  flex-direction: column;
  justify-content: space-between;
  padding: 30px;
  border: 1px solid var(--line);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: var(--shadow-soft);
  transition:
    transform 220ms ease,
    border-color 220ms ease,
    box-shadow 220ms ease;
}

.service-card:hover {
  transform: translateY(-6px);
  border-color: var(--line-strong);
  box-shadow: 0 28px 72px rgba(41, 46, 82, 0.14);
}

.service-card__topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--blue);
}

.service-card__topline span {
  font-size: 0.76rem;
  font-weight: 900;
  letter-spacing: 0.22em;
}

.service-card h3 {
  margin: 0;
  color: var(--navy);
  font-size: 2.25rem;
  font-weight: 900;
  letter-spacing: -0.05em;
}

.service-card p {
  margin: 18px 0 0;
  color: var(--text-soft);
  line-height: 1.7;
}

.service-card ul {
  display: grid;
  gap: 11px;
  margin: 34px 0 0;
  padding: 0;
  list-style: none;
}

.service-card li {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text);
  font-size: 0.9rem;
  font-weight: 780;
}

.service-card li svg {
  flex: 0 0 auto;
  color: var(--blue);
}

.about__grid {
  display: grid;
  grid-template-columns: minmax(320px, 0.9fr) minmax(0, 1.1fr);
  align-items: center;
  gap: clamp(52px, 7vw, 96px);
}

.image-block {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 28px;
  background: #e4e5ed;
  box-shadow: var(--shadow-soft);
}

.image-block--tall {
  min-height: 570px;
}

.about-photo::before {
  content: '';
  position: absolute;
  z-index: -1;
  inset: 12% -9% -8% 12%;
  border-radius: 40px;
  background: rgba(55, 61, 107, 0.15);
  filter: blur(48px);
}

.about-photo__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(0.95) contrast(1.03);
  transition: transform 700ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.about-photo:hover .about-photo__image {
  transform: scale(1.025);
}

.about-photo__overlay {
  position: absolute;
  inset: 0;
  z-index: 1;
  background:
    linear-gradient(180deg, transparent 50%, rgba(23, 26, 49, 0.84) 100%),
    linear-gradient(135deg, rgba(55, 61, 107, 0.1), transparent 45%);
}

.image-block__content {
  position: absolute;
  z-index: 2;
  inset: auto 0 0;
  padding: 28px;
}

.image-block__content p {
  margin: 0 0 7px;
  color: #c7cada;
  font-size: 0.69rem;
  font-weight: 900;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}

.image-block__content h3 {
  margin: 0;
  color: #ffffff;
  font-size: 2rem;
  font-weight: 900;
  letter-spacing: -0.045em;
}

.about__content h2 {
  max-width: 760px;
  margin-top: 14px;
  font-size: clamp(3.4rem, 5.7vw, 6.2rem);
}

.about__text {
  display: grid;
  gap: 18px;
  max-width: 700px;
  margin-top: 30px;
}

.about__text p {
  margin: 0;
  color: var(--text-soft);
  font-size: 1.04rem;
  line-height: 1.78;
}

blockquote {
  position: relative;
  overflow: hidden;
  margin: 36px 0 0;
  padding: 28px 30px;
  border: 1px solid var(--line-strong);
  border-radius: 22px;
  background: linear-gradient(135deg, #fafafe, #f0f1f7);
  box-shadow: 0 16px 38px rgba(55, 61, 107, 0.08);
}

blockquote::before {
  content: '“';
  position: absolute;
  top: -14px;
  right: 20px;
  color: rgba(55, 61, 107, 0.09);
  font-size: 8rem;
  font-weight: 900;
  line-height: 1;
}

blockquote p {
  position: relative;
  max-width: 680px;
  margin: 0;
  color: var(--navy);
  font-size: clamp(1.22rem, 1.8vw, 1.48rem);
  font-weight: 850;
  letter-spacing: -0.025em;
  line-height: 1.3;
}

blockquote cite {
  display: block;
  margin-top: 16px;
  color: var(--blue);
  font-size: 0.71rem;
  font-style: normal;
  font-weight: 900;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.about-more-button {
  margin-top: 28px;
}

.logo-trustbar {
  position: relative;
  z-index: 1;
  border-block: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 10px 34px rgba(41, 46, 82, 0.04);
  backdrop-filter: blur(16px);
}

.logo-trustbar__inner {
  padding-block: 30px;
}

.logo-trustbar__title {
  margin: 0 0 22px;
  color: var(--text-muted);
  font-size: 0.7rem;
  font-weight: 900;
  letter-spacing: 0.21em;
  text-transform: uppercase;
}

.logo-trustbar__logos {
  width: 100%;
  overflow: hidden;
}

.logo-trustbar__track {
  display: flex;
  width: 100%;
}

.logo-trustbar__group {
  display: flex;
  min-width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: clamp(24px, 4vw, 60px);
}

/* Auf Desktop reicht eine ruhige, statische Logo-Reihe. */
.logo-trustbar__group:nth-child(2) {
  display: none;
}

.logo-trustbar__item {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 58px;
  opacity: 0.54;
  filter: grayscale(1) contrast(0.86);
  transition:
    opacity 200ms ease,
    filter 200ms ease,
    transform 200ms ease;
}

.logo-trustbar__item:hover {
  opacity: 1;
  filter: grayscale(0) contrast(1);
  transform: translateY(-3px);
}

.logo-trustbar__logo {
  display: block;
  max-width: 100%;
  max-height: 42px;
  object-fit: contain;
}

.work {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0), rgba(240, 241, 247, 0.44));
}

.work-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  grid-auto-flow: dense;
  grid-auto-rows: 118px;
  gap: 16px;
}

.work-grid__item {
  position: relative;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
  padding: 0;
  border: 1px solid var(--line);
  border-radius: 22px;
  background: #e8e9ef;
  box-shadow: 0 16px 42px rgba(41, 46, 82, 0.1);
  cursor: zoom-in;
  transition:
    transform 240ms ease,
    border-color 240ms ease,
    box-shadow 240ms ease;
}

/* Minimalistischer Ladezustand:
   ein sehr dezentes Licht wandert über die Fläche, bis das Bild bereit ist. */
.work-grid__item::before {
  content: '';
  position: absolute;
  z-index: 1;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(
      105deg,
      rgba(255, 255, 255, 0) 34%,
      rgba(255, 255, 255, 0.48) 48%,
      rgba(255, 255, 255, 0) 62%
    ),
    linear-gradient(135deg, #ececf2, #e3e4eb);
  background-size: 220% 100%, 100% 100%;
  background-position: 160% 0, 0 0;
  opacity: 1;
  transition: opacity 420ms ease;
  animation: work-image-loading 1.35s ease-in-out infinite;
}

.work-grid__item.is-loaded::before {
  opacity: 0;
  animation-play-state: paused;
}

@keyframes work-image-loading {
  0% {
    background-position: 160% 0, 0 0;
  }

  100% {
    background-position: -70% 0, 0 0;
  }
}

.work-grid__item:hover {
  z-index: 2;
  transform: translateY(-5px);
  border-color: rgba(55, 61, 107, 0.34);
  box-shadow: 0 25px 58px rgba(41, 46, 82, 0.16);
}

.work-grid__item--feature {
  grid-column: span 7;
  grid-row: span 4;
}

.work-grid__item--portrait {
  grid-column: span 5;
  grid-row: span 4;
}

.work-grid__item--wide {
  grid-column: span 6;
  grid-row: span 3;
}

.work-grid__item--small {
  grid-column: span 3;
  grid-row: span 3;
}

.work-grid__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transform: scale(1.012);
  transition:
    opacity 460ms ease,
    transform 620ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.work-grid__image.is-loaded {
  opacity: 1;
  transform: scale(1);
}

.work-grid__item:hover .work-grid__image.is-loaded {
  transform: scale(1.035);
}

.work-grid__zoom {
  position: absolute;
  right: 14px;
  bottom: 14px;
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.36);
  border-radius: 999px;
  background: rgba(23, 26, 49, 0.62);
  color: #ffffff;
  opacity: 0;
  transform: translateY(8px);
  backdrop-filter: blur(12px);
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}

.work-grid__item:hover .work-grid__zoom,
.work-grid__item:focus-visible .work-grid__zoom {
  opacity: 1;
  transform: translateY(0);
}

.work-empty {
  padding: 30px;
  border: 1px solid var(--line);
  border-radius: 22px;
  background: var(--surface);
  color: var(--text-soft);
}

.work-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 38px;
}

.process {
  border-top: 1px solid rgba(55, 61, 107, 0.08);
}

.process-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-block: 1px solid var(--line);
}

.process-card {
  padding: 34px 30px;
  border-right: 1px solid var(--line);
}

.process-card:last-child {
  border-right: 0;
}

.process-card__number {
  color: var(--blue);
  font-size: 0.74rem;
  font-weight: 900;
  letter-spacing: 0.22em;
}

.process-card h3 {
  margin: 38px 0 0;
  color: var(--navy);
  font-size: 1.85rem;
  font-weight: 900;
  letter-spacing: -0.045em;
}

.process-card p {
  margin: 15px 0 0;
  color: var(--text-soft);
  line-height: 1.72;
}

.contact {
  padding-bottom: 48px;
}

.contact-card {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(250px, 0.42fr);
  align-items: end;
  gap: 52px;
  overflow: hidden;
  padding: clamp(38px, 5vw, 62px);
  border: 1px solid rgba(98, 108, 159, 0.22);
  border-radius: 30px;
  background:
    radial-gradient(circle at 88% 14%, rgba(55, 61, 107, 0.34), transparent 25rem),
    linear-gradient(135deg, #171a31, #242a4c);
  box-shadow: 0 28px 74px rgba(23, 26, 49, 0.25);
}

.contact-card::after {
  content: '';
  position: absolute;
  right: -110px;
  bottom: -150px;
  width: 340px;
  height: 340px;
  border: 1px solid rgba(98, 108, 159, 0.18);
  border-radius: 50%;
}

.contact-card > * {
  position: relative;
  z-index: 1;
}

.section-kicker--light {
  color: #aeb3cf;
}

.contact-card h2 {
  max-width: 790px;
  margin-top: 14px;
  color: #ffffff;
  font-size: clamp(3.7rem, 6.8vw, 7rem);
}

.contact-card p:not(.section-kicker) {
  max-width: 670px;
  margin: 24px 0 0;
  color: rgba(226, 236, 251, 0.74);
  font-size: 1.04rem;
  line-height: 1.76;
}

.contact-card__actions {
  display: grid;
  gap: 12px;
}

.footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-top: 36px;
  padding-top: 28px;
  border-top: 1px solid var(--line);
  color: var(--text-muted);
  font-size: 0.86rem;
}

.footer p {
  margin: 0;
}

.lightbox {
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 72px clamp(18px, 5vw, 72px);
  background: rgba(3, 10, 22, 0.92);
  backdrop-filter: blur(18px);
}

.lightbox__figure {
  display: grid;
  justify-items: center;
  max-width: min(1180px, 86vw);
  max-height: 82vh;
  margin: 0;
}

.lightbox__image {
  display: block;
  max-width: 100%;
  max-height: 76vh;
  border-radius: 18px;
  object-fit: contain;
  box-shadow: 0 30px 100px rgba(0, 0, 0, 0.44);
}

.lightbox figcaption {
  margin-top: 14px;
  color: rgba(255, 255, 255, 0.66);
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.lightbox__close,
.lightbox__nav {
  position: absolute;
  display: grid;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  cursor: pointer;
  backdrop-filter: blur(12px);
  transition:
    background 180ms ease,
    transform 180ms ease;
}

.lightbox__close:hover,
.lightbox__nav:hover {
  background: rgba(255, 255, 255, 0.16);
}

.lightbox__close {
  top: 22px;
  right: 22px;
  width: 46px;
  height: 46px;
  border-radius: 999px;
}

.lightbox__nav {
  top: 50%;
  width: 50px;
  height: 50px;
  border-radius: 999px;
}

.lightbox__nav--previous {
  left: 22px;
  transform: translateY(-50%);
}

.lightbox__nav--next {
  right: 22px;
  transform: translateY(-50%);
}

.lightbox__nav--previous:hover,
.lightbox__nav--next:hover {
  transform: translateY(-50%) scale(1.04);
}

.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
  transition: opacity 180ms ease;
}

.lightbox-fade-enter-from,
.lightbox-fade-leave-to {
  opacity: 0;
}

@keyframes logo-marquee {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(-50%);
  }
}

@media (max-width: 1040px) {
  .hero {
    min-height: auto;
  }

  .hero__grid,
  .about__grid,
  .contact-card {
    grid-template-columns: 1fr;
  }

  .hero__copy {
    max-width: 680px;
  }

  .services-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .service-card:last-child {
    grid-column: 1 / -1;
    min-height: 330px;
  }

  .about-photo {
    max-width: 720px;
  }

  .contact-card__actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-width: 570px;
  }

  .work-grid {
    grid-template-columns: repeat(6, minmax(0, 1fr));
    grid-auto-rows: 115px;
  }

  .work-grid__item--feature,
  .work-grid__item--wide {
    grid-column: span 6;
  }

  .work-grid__item--portrait {
    grid-column: span 3;
  }

  .work-grid__item--small {
    grid-column: span 3;
  }
}

@media (max-width: 760px) {
  .container {
    width: min(calc(100% - 32px), 1240px);
  }

  .logo-trustbar__logos {
    /* Die Logos dürfen bis an den mobilen Rand laufen, ohne abgeschnitten zu wirken. */
    width: calc(100% + 32px);
    margin-inline: -16px;
    mask-image: linear-gradient(
      to right,
      transparent 0,
      #000 6%,
      #000 94%,
      transparent 100%
    );
    -webkit-mask-image: linear-gradient(
      to right,
      transparent 0,
      #000 6%,
      #000 94%,
      transparent 100%
    );
  }

  .logo-trustbar__track {
    width: max-content;
    animation: logo-marquee 24s linear infinite;
    will-change: transform;
  }

  .logo-trustbar__group,
  .logo-trustbar__group:nth-child(2) {
    display: flex;
    min-width: max-content;
    justify-content: flex-start;
    gap: 30px;
    padding-right: 30px;
  }

  .logo-trustbar__item {
    width: 82px;
    height: 54px;
  }

  .logo-trustbar__logo {
    max-height: 38px;
  }

  .section {
    padding: 82px 0;
  }

  .hero {
    padding-top: 128px;
    padding-bottom: 76px;
  }

  .hero__brand {
    max-width: 720px;
  }

  .hero__logo {
    width: min(100%, 720px);
  }

  .hero__actions,
  .contact-card__actions {
    grid-template-columns: 1fr;
  }

  .hero__actions .button,
  .contact-card__actions .button {
    width: 100%;
  }

  .section-header,
  .services-grid,
  .process-grid {
    grid-template-columns: 1fr;
  }

  .section-header {
    gap: 14px;
  }

  .service-card:last-child {
    grid-column: auto;
  }

  .service-card {
    min-height: 360px;
  }

  .image-block--tall {
    min-height: 500px;
  }

  .process-grid {
    border-block: 1px solid var(--line);
  }

  .process-card {
    border-right: 0;
    border-bottom: 1px solid var(--line);
  }

  .process-card:last-child {
    border-bottom: 0;
  }

  .contact-card {
    gap: 36px;
    border-radius: 24px;
  }

  .footer {
    align-items: flex-start;
    flex-direction: column;
  }

  .lightbox__nav {
    top: auto;
    bottom: 22px;
  }

  .lightbox__nav--previous {
    left: calc(50% - 60px);
    transform: none;
  }

  .lightbox__nav--next {
    right: calc(50% - 60px);
    transform: none;
  }

  .lightbox__nav--previous:hover,
  .lightbox__nav--next:hover {
    transform: scale(1.04);
  }
}

@media (max-width: 620px) {
  .eyebrow {
    letter-spacing: 0.17em;
  }

  .hero__copy > p:first-child,
  .about__text p,
  .section-header__text,
  .contact-card p:not(.section-kicker) {
    font-size: 1rem;
  }

  .work-grid {
    grid-template-columns: 1fr;
    grid-auto-rows: 290px;
  }

  /* Mobil reichen fünf Vorschaubilder. Weitere Bilder gibt es per Button/Lightbox. */
  .work-grid__item:nth-child(n + 6) {
    display: none;
  }

  .work-grid__item--feature,
  .work-grid__item--portrait,
  .work-grid__item--wide,
  .work-grid__item--small {
    grid-column: span 1;
    grid-row: span 1;
  }

  .work-grid__zoom {
    opacity: 1;
    transform: none;
  }

  .logo-trustbar__inner {
    padding-block: 24px;
  }
}

@media (prefers-reduced-motion: reduce) {
  :global(html) {
    scroll-behavior: auto;
  }

  .logo-trustbar__track {
    animation: none;
  }

  .work-grid__item::before {
    animation: none;
  }

  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>